import { useState, useCallback, useEffect } from 'react';
import { useAntiSpam, defaultAntiSpamConfig } from './useAntiSpam';

interface Question {
  x: number;
  y: number;
  answer: number;
}

interface GameStats {
  viewed: number;
  skipped: number;
  total: number;
}

interface Range {
  min: number;
  max: number;
}

export const useGameLogic = () => {
  const [range, setRange] = useState<Range>({ min: 1, max: 10 });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    viewed: 0,
    skipped: 0,
    total: 0,
  });

  // Initialize anti-spam protection
  const { checkAction, isBlocked } = useAntiSpam(defaultAntiSpamConfig);

  const generateQuestion = useCallback((): Question => {
    const x = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    const y = Math.floor(Math.random() * 8) + 2;
    return { x, y, answer: x * y };
  }, [range]);

  useEffect(() => {
    const newQuestion = generateQuestion();
    setQuestions([newQuestion]);
    setCurrentIndex(0);
    setShowAnswer(false);
  }, [range, generateQuestion]);

  const addNewQuestion = useCallback(() => {
    setQuestions(prev => [...prev, generateQuestion()]);
  }, [generateQuestion]);

  const setCustomRange = (newRange: Range) => {
    if (newRange.min > 0 && newRange.max > 0 && newRange.min <= newRange.max) {
      setRange(newRange);
      setQuestions([]);
      setCurrentIndex(0);
      setShowAnswer(false);
      setStats({ viewed: 0, skipped: 0, total: 0 });
    }
  };

  const skipQuestion = () => {
    if (!checkAction()) return; // Anti-spam check

    try {
      const g = (window as any).gtag;
      if (typeof g === 'function') {
        g('event', 'game_skip', { range_min: range.min, range_max: range.max });
      }
    } catch {}
    setStats(prev => ({ ...prev, skipped: prev.skipped + 1, total: prev.total + 1 }));
    setShowAnswer(false);
    if (currentIndex === questions.length - 1) {
      addNewQuestion();
    }
    setCurrentIndex(prev => prev + 1);
  };

  const previousQuestion = () => {
    if (!checkAction()) return; // Anti-spam check

    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const nextQuestion = () => {
    if (!checkAction()) return; // Anti-spam check

    if (!showAnswer) {
      setShowAnswer(true);
      try {
        const g = (window as any).gtag;
        if (typeof g === 'function') {
          g('event', 'game_viewed', { range_min: range.min, range_max: range.max });
        }
      } catch {}
      setStats(prev => ({ ...prev, viewed: prev.viewed + 1, total: prev.total + 1 }));
    } else {
      setShowAnswer(false);
      if (currentIndex === questions.length - 1) {
        addNewQuestion();
      }
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleAction = () => {
    if (!checkAction()) return;
    // Perform the action
  };

  return {
    range,
    setCustomRange,
    currentQuestion: questions[currentIndex] || generateQuestion(),
    showAnswer,
    skipQuestion,
    previousQuestion,
    nextQuestion,
    stats,
    isBlocked, // Expose blocked state to UI
    handleAction,
  };
};